import React, { useState, useEffect } from 'react';
import TagManager from './TagManager';

const ItemModal = ({ item, onClose }) => {
  const [formData, setFormData] = useState({
    brand: '',
    name: '',
    category_id: '',
    parent_category_id: '', // 新增：一级分类
    tags: [],
    notes: '',
    is_favorite: false
  });
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [parentCategories, setParentCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);

  useEffect(() => {
    if (item && categories.length > 0) {
      // 找到当前分类的父分类
      const currentCategory = categories.find(c => c.id === item.category_id);
      const parentCategoryId = currentCategory?.parent_id || '';
      
      setFormData({
        brand: item.brand || '',
        name: item.name || '',
        category_id: item.category_id || '',
        parent_category_id: parentCategoryId,
        tags: item.tags || [],
        notes: item.notes || '',
        is_favorite: item.is_favorite || false
      });
      
      // 分离一级和二级分类
      const parents = categories.filter(cat => !cat.parent_id);
      setParentCategories(parents);
    }
  }, [item, categories.length]);

  useEffect(() => {
    loadCategories();
  }, [item]);

  const loadCategories = async () => {
    try {
      const { db, auth } = await import('../supabaseClient');
      const user = await auth.getCurrentUser();
      const { data } = await db.getCategories(user.id);
      setCategories(data || []);
    } catch (error) {
      console.error('加载分类失败:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'parent_category_id') {
      const selectedValue = e.target.value;
      // 更新子分类列表
      const children = selectedValue ? categories.filter(cat => cat.parent_id === selectedValue) : [];
      setChildCategories(children);
      
      setFormData(prev => ({
        ...prev,
        parent_category_id: selectedValue,
        category_id: '', // 清空二级分类
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { db, auth } = await import('../supabaseClient');
      const user = await auth.getCurrentUser();

      // 检查用户是否登录
      if (!user || !user.id) {
        alert('用户未登录，请重新登录');
        setLoading(false);
        return;
      }

      // 检查是否选择了分类
      if (!formData.parent_category_id) {
        alert('请先选择分类，或者去分类管理页面创建分类');
        setLoading(false);
        return;
      }

      // 重写提交逻辑：纯文本处理
      const categoryId = formData.category_id && formData.category_id !== '' 
        ? formData.category_id 
        : formData.parent_category_id;
      
      const parentCategoryId = formData.parent_category_id === '' 
        ? null 
        : formData.parent_category_id;

      // 纯文本提交对象
      const itemData = {
        brand: formData.brand.trim(),              // 品牌字符串
        name: formData.name.trim(),                // 名称字符串
        category_id: categoryId,                   // 分类 ID 字符串
        parent_category_id: parentCategoryId,         // 父分类 ID 字符串或 null
        tags: formData.tags || [],                 // 标签数组
        remarks: formData.notes?.trim() || null,    // 备注字符串或 null
        user_id: user.id                          // 用户 ID 字符串
      };

      console.log('提交的纯文本数据:', itemData);

      let result;
      if (item?.id) {
        // 更新物品
        result = await db.updateItem(item.id, itemData);
      } else {
        // 添加物品
        result = await db.addItem(itemData);
      }

      if (result.error) {
        console.error('保存失败:', result.error);
        alert('保存失败: ' + result.error.message);
        throw new Error(result.error.message);
      }

      onClose();
    } catch (error) {
      console.error('保存失败:', error);
      alert('保存失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const buildCategoryOptions = (categories) => {
    // 获取所有一级分类
    const rootCategories = categories.filter(cat => !cat.parent_id);
    
    return rootCategories.map(category => {
      // 获取该分类的子分类
      const childCategories = categories.filter(cat => cat.parent_id === category.id);
      
      return (
        <optgroup key={category.id} label={`${category.icon || '📦'} ${category.name}`}>
          <option value={category.id}>
            {category.icon || '📦'} {category.name}
          </option>
          {childCategories.map(child => (
            <option key={child.id} value={child.id}>
              {'  '}{child.icon || '📁'} {child.name}
            </option>
          ))}
        </optgroup>
      );
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#1F1F1F', border: '1px solid #2D2D2D' }}>
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-6" style={{ color: '#FFFFFF' }}>
            {item?.id ? '编辑物品' : '添加物品'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 基本信息 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  品牌 *
                </label>
                <input
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={handleInputChange}
                  autoComplete="off"
                  className="w-full px-4 py-3 rounded-lg"
                  style={{ 
                    backgroundColor: '#1F1F1F', 
                    border: '1px solid #2D2D2D',
                    color: '#FFFFFF'
                  }}
                  placeholder="如：SK-II"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  名称 *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  autoComplete="off"
                  className="w-full px-4 py-3 rounded-lg"
                  style={{ 
                    backgroundColor: '#1F1F1F', 
                    border: '1px solid #2D2D2D',
                    color: '#FFFFFF'
                  }}
                  placeholder="如：小棕瓶精华"
                  required
                />
              </div>
            </div>

            {/* 分类 - 级联选择 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  一级分类 *
                </label>
                <select
                  name="parent_category_id"
                  value={formData.parent_category_id}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg"
                  style={{ 
                    backgroundColor: '#1F1F1F', 
                    border: '1px solid #2D2D2D',
                    color: '#FFFFFF'
                  }}
                  required
                >
                  <option value="">请选择一级分类</option>
                  {parentCategories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.icon || '📦'} {category.name}
                    </option>
                  ))}
                </select>
              </div>

              {childCategories.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    二级分类
                  </label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg"
                    style={{ 
                      backgroundColor: '#1F1F1F', 
                      border: '1px solid #2D2D2D',
                      color: '#FFFFFF'
                    }}
                  >
                    <option value="">请选择二级分类</option>
                    {childCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.icon || '📁'} {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* 标签 */}
            <TagManager
              selectedTags={formData.tags}
              onTagsChange={(tags) => setFormData(prev => ({ ...prev, tags }))}
            />

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                备注
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-3 rounded-lg resize-none"
                style={{ 
                  backgroundColor: '#1F1F1F', 
                  border: '1px solid #2D2D2D',
                  color: '#FFFFFF'
                }}
                placeholder="添加备注信息..."
              />
            </div>

            {/* 按钮 */}
            <div className="flex items-center justify-end space-x-4 pt-6" style={{ borderTop: '1px solid #2D2D2D' }}>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 rounded-lg transition-colors"
                style={{ backgroundColor: '#4A4A4A', color: '#FFFFFF' }}
              >
                取消
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 rounded-lg font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#FFFFFF', color: '#121212' }}
              >
                {loading ? '保存中...' : (item?.id ? '更新物品' : '添加物品')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ItemModal;
